import { AnyAction } from 'redux';
import { ThunkAction } from 'redux-thunk';
import api from '../utils/router';

type CategoryID = number;
type ProductID = number;
type ShowID = number;

export type Category = {
    id: number;
    name: string;
    description: string;
    product_count: number;
};

type TaxedMoney = {
    formatted: {
        gross: string;
        net: string;
        tax: string;
    };
    gross: number;
    net: number;
    tax: number;
};

export type Product = {
    id: ProductID;
    category_id: number;

    name: string;
    description: string;
    thumbnail: string;
    photo: string;

    price: TaxedMoney;
    currency: string;
};

// TS1336 does not allow to use a type alias in an index signature. This is why we use number here.
//  see https://github.com/microsoft/TypeScript/issues/24437
type CategoryMap = { [categoryId: number]: Category };
type ProductMap = { [productId: number]: Product };

// Array of CategoryIDs in the order they were returned from API
type CategoryIdList = Array<CategoryID>;

// Map of categoryId to an array of productIds in the order they were returned from API
type ProductsByCategory = {
    [categoryId: number]: {
        items: Array<ProductID>;
        cursorNext: string | null;
    };
};

type IntermissionShopState = {
    products: ProductMap;
    category: CategoryMap;

    categories: CategoryIdList;
    isCategoriesStartLoad: boolean;
    productsByCategory: ProductsByCategory;
    isProductsStartLoad: boolean;
};

const initialState: IntermissionShopState = {
    category: {},
    categories: [],
    isCategoriesStartLoad: true,

    products: {},
    productsByCategory: {},
    isProductsStartLoad: true,
};

type LoadedCategoriesAction = {
    type: 'intermission/loaded-categories';
    categories: Array<Category>;
};

type LoadedProductsAction = {
    type: 'intermission/loaded-products';
    categoryId: CategoryID;
    showId: ShowID;
    products: Array<Product>;
    cursorNext: string | null;
    reset: boolean;
};

type StartLoadProductsAction = {
    type: 'intermission/start-load-products';
};

type IntermissionAction =
    | LoadedCategoriesAction
    | LoadedProductsAction
    | StartLoadProductsAction;

const asCategoryMap = (items: Array<Category>) => {
    const res: CategoryMap = {};

    for (let category of items) {
        res[category.id] = category;
    }

    return res;
};

const asProductMap = (items: Array<Product>) => {
    const res: ProductMap = {};

    for (let product of items) {
        res[product.id] = product;
    }

    return res;
};

const mergeIds = (
    originalArr: Array<number>,
    newArr: Array<number>,
    reset: boolean = false,
): Array<number> => {
    if (reset) {
        return newArr;
    }
    const merged = [...originalArr];
    newArr.forEach(item => {
        if (merged.indexOf(item) === -1) {
            merged.push(item);
        }
    });

    return merged;
};

export const shopReducer = (
    state = initialState,
    action: IntermissionAction,
): IntermissionShopState => {
    switch (action.type) {
        case 'intermission/loaded-categories':
            return {
                ...state,
                isCategoriesStartLoad: false,
                categories: action.categories.map(
                    (category: Category) => category.id,
                ),
                category: {
                    ...state.category,
                    ...asCategoryMap(action.categories),
                },
            };

        case 'intermission/start-load-products':
            return {
                ...state,
                isProductsStartLoad: true,
            };

        case 'intermission/loaded-products':
            return {
                ...state,
                isProductsStartLoad: false,
                productsByCategory: {
                    ...state.productsByCategory,

                    [action.categoryId]: {
                        cursorNext: action.cursorNext,
                        items: mergeIds(
                            state.productsByCategory[action.categoryId]
                                ?.items || [],
                            action.products.map(
                                (product: Product) => product.id,
                            ),
                            action.reset,
                        ),
                    },
                },

                products: {
                    ...state.products,
                    ...asProductMap(action.products),
                },
            };

        default:
            return state;
    }
};

export type AppState = { shop: IntermissionShopState };

export type AppThunk = ThunkAction<
    Promise<void>,
    AppState,
    undefined,
    AnyAction
>;

export const fetchCategories = (): AppThunk => async dispatch => {
    const categories = await api.categories.fetch();

    dispatch({
        type: 'intermission/loaded-categories',
        categories,
    });
};

export const selectCategories = (state: AppState): Array<Category> => {
    return state.shop.categories.map(
        (id: CategoryID): Category => state.shop.category[id],
    );
};

export const selectIsCategoriesStartLoad = (state: AppState): boolean =>
    state.shop.isCategoriesStartLoad;
